import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { ChevronLeft, ChevronRight, Sun, Moon, X, Briefcase, Car, Clock } from "lucide-react";

const MONTHS = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const DOW = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];
export const SHIFT_META = {
  DAY: { label: "Turno Giorno", short: "Giorno", cls: "bg-emerald-600 border-emerald-400/60", Icon: Sun },
  NIGHT: { label: "Turno Notte", short: "Notte", cls: "bg-[#1F6BFF] border-sky-300/60", Icon: Moon },
  REST: { label: "Riposo", short: "Riposo", cls: "bg-[#7A1420] border-rose-400/50", Icon: X },
  SICK: { label: "Malattia", short: "Malattia", cls: "bg-violet-700 border-violet-300/50", Icon: Briefcase },
  VACATION: { label: "Ferie", short: "Ferie", cls: "bg-violet-700 border-violet-300/50", Icon: Briefcase },
  LEAVE: { label: "Permesso", short: "Permesso", cls: "bg-violet-700 border-violet-300/50", Icon: Briefcase },
};
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const monday = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };

function useShifts(months) {
  const [map, setMap] = useState({});
  const [err, setErr] = useState(false);
  const key = months.join(",");
  useEffect(() => {
    let off = false;
    Promise.all(months.map(k => { const [y, m] = k.split("-"); return api.get("/shifts", { params: { year: +y, month: +m } }); }))
      .then(rs => { if (!off) { const o = {}; rs.forEach(r => (r.data || []).forEach(s => { o[s.date] = s; })); setMap(o); setErr(false); } })
      .catch(() => { if (!off) setErr(true); });
    return () => { off = true; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return { map, err };
}

function DayCell({ d, s, inMonth, today, selected, onPick }) {
  const m = s && SHIFT_META[s.shift_type];
  return (
    <button type="button" onClick={() => onPick(iso(d))} data-testid={`shift-day-${iso(d)}`} data-shift={s?.shift_type || "none"}
      className={`relative aspect-square min-h-[40px] rounded-lg border flex flex-col items-center justify-center transition-shadow
        ${m ? m.cls : "bg-white/[0.03] border-white/10"} ${inMonth ? "" : "opacity-35"}
        ${selected ? "ring-2 ring-[#5AA2FF] shadow-[0_0_14px_rgba(90,162,255,0.7)]" : ""}`}>
      <span className={`font-mono-num text-sm font-extrabold leading-none ${m ? "text-white" : "text-slate-400"}`}>{d.getDate()}</span>
      {m && <m.Icon size={11} strokeWidth={s.shift_type === "REST" ? 3.5 : 2.2} className={`mt-0.5 ${s.shift_type === "REST" ? "text-rose-200" : "text-white/90"}`} />}
      {today && <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-[#E9C46A]" data-testid="shift-today-dot" />}
    </button>
  );
}

function Legend() {
  const items = [["bg-emerald-600", "Giorno", Sun], ["bg-[#1F6BFF]", "Notte", Moon], ["bg-[#7A1420]", "Riposo", X], ["bg-violet-700", "Ferie / Malattia / Permesso", Briefcase], ["bg-white/[0.06] border border-white/15", "Nessuna assegnazione", null]];
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-[11px] text-slate-300" data-testid="shift-legend">
      {items.map(([c, l, I]) => <span key={l} className="inline-flex items-center gap-1.5"><span className={`w-4 h-4 rounded ${c} flex items-center justify-center`}>{I && <I size={9} className="text-white" strokeWidth={3} />}</span>{l}</span>)}
      <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#E9C46A]" />Oggi</span>
    </div>
  );
}

function DayDetail({ day, s }) {
  const m = s && SHIFT_META[s.shift_type];
  const d = new Date(`${day}T12:00:00`);
  return (
    <div className="rounded-xl border border-[#2F7BFF]/40 bg-[#0A1630] p-3.5 flex items-center gap-3" data-testid="shift-day-detail">
      <div className={`w-11 h-11 rounded-xl border flex items-center justify-center shrink-0 ${m ? m.cls : "bg-white/5 border-white/10"}`}>{m ? <m.Icon size={20} className="text-white" /> : <Clock size={18} className="text-slate-500" />}</div>
      <div className="min-w-0">
        <div className="text-[11px] text-slate-400 capitalize">{d.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</div>
        <div className="text-white font-bold" data-testid="shift-day-detail-type">{m ? m.label : "Nessuna assegnazione"}</div>
        {s && (s.time_start || s.time_end) && <div className="text-xs text-slate-300 font-mono-num">{s.time_start || "--:--"} – {s.time_end || "--:--"}</div>}
        {s?.vehicle_plate && <div className="text-xs text-[#E9C46A] inline-flex items-center gap-1"><Car size={11} />{s.vehicle_plate}</div>}
        {s?.note && <div className="text-xs text-slate-400">{s.note}</div>}
      </div>
    </div>
  );
}

export function ShiftCalendar() {
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [weekStart, setWeekStart] = useState(() => monday(new Date()));
  const [sel, setSel] = useState(iso(new Date()));
  const todayIso = iso(new Date());
  const cells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = monday(first);
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [cursor]);
  const week = useMemo(() => Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d; }), [weekStart]);
  const span = view === "month" ? [cells[0], cells[41]] : [week[0], week[6]];
  const months = [...new Set([...(view === "month" ? [cells[0], cursor, cells[41]] : span)].map(d => `${d.getFullYear()}-${d.getMonth() + 1}`))];
  const { map, err } = useShifts(months);
  const pick = (k) => setSel(k);
  const navBtn = "w-10 h-10 rounded-lg border border-white/10 text-slate-300 hover:text-white flex items-center justify-center";
  return (
    <section className="rounded-2xl border border-[#2F7BFF]/25 bg-[#081226] p-4 sm:p-5 space-y-4" data-testid="driver-shift-calendar">
      <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-black/40 border border-white/10">
        {[["month", "Mese"], ["week", "Settimana"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setView(k)} data-testid={`shift-view-${k}`} className={`min-h-[40px] rounded-lg text-sm font-bold ${view === k ? "bg-[#1F6BFF] text-white shadow-[0_0_14px_rgba(31,107,255,0.55)]" : "text-slate-400"}`}>{l}</button>
        ))}
      </div>
      {view === "month" ? (
        <>
          <div className="flex items-center justify-between">
            <button type="button" className={navBtn} onClick={() => setCursor(c => new Date(c.getFullYear(), c.getMonth() - 1, 1))} data-testid="shift-month-prev"><ChevronLeft size={16} /></button>
            <div className="font-display text-lg text-white font-bold" data-testid="shift-month-label">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</div>
            <button type="button" className={navBtn} onClick={() => setCursor(c => new Date(c.getFullYear(), c.getMonth() + 1, 1))} data-testid="shift-month-next"><ChevronRight size={16} /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
            {DOW.map(d => <div key={d} className="text-center text-[10px] uppercase tracking-wider text-slate-500 font-mono-num">{d}</div>)}
            {cells.map(d => <DayCell key={iso(d)} d={d} s={map[iso(d)]} inMonth={d.getMonth() === cursor.getMonth()} today={iso(d) === todayIso} selected={iso(d) === sel} onPick={pick} />)}
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <button type="button" className={navBtn} onClick={() => setWeekStart(w => { const n = new Date(w); n.setDate(n.getDate() - 7); return n; })} data-testid="shift-week-prev"><ChevronLeft size={16} /></button>
            <div className="text-white font-bold text-sm font-mono-num" data-testid="shift-week-label">{week[0].getDate()}/{week[0].getMonth() + 1} – {week[6].getDate()}/{week[6].getMonth() + 1}/{week[6].getFullYear()}</div>
            <button type="button" className={navBtn} onClick={() => setWeekStart(w => { const n = new Date(w); n.setDate(n.getDate() + 7); return n; })} data-testid="shift-week-next"><ChevronRight size={16} /></button>
          </div>
          <div className="space-y-1.5" data-testid="shift-week-list">
            {week.map((d, i) => { const s = map[iso(d)]; const m = s && SHIFT_META[s.shift_type]; return (
              <button key={iso(d)} type="button" onClick={() => pick(iso(d))} data-testid={`shift-week-row-${iso(d)}`}
                className={`w-full min-h-[52px] rounded-xl border px-3 flex items-center gap-3 text-left ${m ? `${m.cls} bg-opacity-90` : "bg-white/[0.03] border-white/10"} ${iso(d) === sel ? "ring-2 ring-[#5AA2FF]" : ""}`}>
                <div className="w-10 text-center"><div className="text-[10px] uppercase text-white/70">{DOW[i]}</div><div className="font-mono-num font-extrabold text-white">{d.getDate()}</div></div>
                {m ? <m.Icon size={18} className="text-white" /> : <Clock size={16} className="text-slate-500" />}
                <div className="flex-1 min-w-0"><div className="text-sm font-bold text-white">{m ? m.label : "Nessuna assegnazione"}</div>
                  {s && (s.time_start || s.time_end) && <div className="text-[11px] text-white/80 font-mono-num">{s.time_start || "--:--"} – {s.time_end || "--:--"}</div>}</div>
                {iso(d) === todayIso && <span className="text-[10px] font-bold text-[#E9C46A]">OGGI</span>}
              </button>); })}
          </div>
        </>
      )}
      {err && <div className="text-xs text-rose-300" data-testid="shift-error">Impossibile caricare i turni. Riprova tra poco.</div>}
      <DayDetail day={sel} s={map[sel]} />
      <Legend />
    </section>
  );
}
